import axios, { AxiosRequestConfig, Method } from "axios"
import { mapToCamelCase, refreshToken } from "../utility"
import { errorFilledToastOptions, UNAUTHORIZED_ERROR_NAME } from "../constants"
import { Action, Dispatch } from "redux"
import { toast } from "react-toastify"
import { AUTH_ACTION } from "../actions/loginActions"
import { CoManagerBankDetails, CoManagerClaimSupport } from "../types"

// Global interceptor to suppress canceled request errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.message === "canceled" || error.code === "ERR_CANCELED" || error.name === "CanceledError") {
      // Silently ignore canceled requests
      return new Promise(() => {})
    }
    return Promise.reject(error)
  }
)

const createRequest = (path: string) => {
  const instance = axios.create({
    baseURL: `${process.env.REACT_APP_BASE_URL}${path}`,
    withCredentials: true
  })
  // Add interceptor to suppress canceled request errors
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.message === "canceled" || error.code === "ERR_CANCELED" || error.name === "CanceledError") {
        return new Promise(() => {})
      }
      return Promise.reject(error)
    }
  )
  return instance
}

export const controllers: Record<string, AbortController> = {}

const createAuthorizedRequest = async (path: string, config?: AxiosRequestConfig, abortable = false) => {
  const error = new Error()
  error.name = UNAUTHORIZED_ERROR_NAME
  if (!(await refreshToken())) throw error

  const controllerId = path.split("?")[0]
  const controller = controllers[controllerId]

  if (abortable) {
    if (controller) {
      controller.abort()
    }
    controllers[controllerId] = new AbortController()
  }

  return axios({
    ...config,
    url: `${process.env.REACT_APP_BASE_URL}${path}`,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      "affiliate-user-id": localStorage.getItem("appliedAffiliate") ?? ""
    },
    withCredentials: true,
    ...(abortable && { signal: controllers[controllerId].signal })
  })
}

export const authLogin = createRequest("/login")
export const authRefresh = createRequest("/refresh")
export const codeValidation = createRequest("/code-validation")
export const publicInvitation = createRequest("/public/track-share-holder-invitation/approve_invitation")
export const smartLinks = createRequest(`/public/smart-link`)
export const spotify = createRequest(`/public/spotify`)
export const publicApi = createRequest(`/public/api`)
export const authLogout = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/logout${path}`, { method, data })
export const me = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/me${path}`, { method, data })
export const users = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/users${path}`, { method, data }, true)
export const featureFm = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/feature-fm${path}`, { method, data }, true)
export const crawlers = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/crawler${path}`, { method, data }, true)
export const paymentMethods = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/payment-method${path}`, { method, data })
export const releases = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases${path}`, { method, data }, false)
export const marketing = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/marketing${path}`, { method, data }, false)
export const discoveryMode = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/marketing/discovery-mode${path}`, { method, data }, false)
export const tracks = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases/track${path}`, { method, data }, true)
export const trackShareHolders = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases/track-share-holder${path}`, { method, data }, true)
export const youtubeVideos = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases/youtube-video${path}`, { method, data }, true)
export const youtubeVideoShareHolders = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases/youtube-video-share-holder${path}`, { method, data }, true)
export const summaries = (path: string, method: Method, data: any = null, config: AxiosRequestConfig = {}) =>
  createAuthorizedRequest(`/releases/summary${path}`, { method, data, ...config })
export const trends = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases/trends${path}`, { method, data }, true)
export const userPayments = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/payments/user-payment-history${path}`, { method, data })
export const kahunaPaymentsReceive = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/payments/kahuna-payment-receive${path}`, { method, data })
export const currencyExchange = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/currency-exchange${path}`, { method, data })
export const completeShareHolders = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases/track-share-holder/complete_share_holders/${path}`, { method, data }, true)
export const collaboratorInvitation = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/releases/track-share-holder-invitation${path}`, { method, data }, true)
export const schedulers = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/scheduler${path}`, { method, data })
// abortable defaults to true, which is right for the chat: a second question replaces the first.
// It is wrong for anything sent several times at once, because the shared controller is keyed by path
// and each new call aborts the previous one.
export const assistant = (
  path: string,
  method: Method,
  data: any = null,
  config: AxiosRequestConfig = {},
  abortable = true
) => createAuthorizedRequest(`/assistant${path}`, { method, data, ...config }, abortable)

// Server sent events cannot go through axios, which only hands back a finished body, so the assistant
// chat reads the response stream itself and calls onDelta with every piece as it lands.
export const assistantStream = async (
  path: string,
  data: any,
  onDelta: (text: string) => void
): Promise<{
  action: string | null
  releaseId: number | null
  reportPeriod: string | null
  bankDetails: CoManagerBankDetails | null
  claimSupport: CoManagerClaimSupport | null
  locale: string | null
}> => {
  const unauthorized = new Error()
  unauthorized.name = UNAUTHORIZED_ERROR_NAME
  if (!(await refreshToken())) throw unauthorized

  const response = await fetch(`${process.env.REACT_APP_BASE_URL}/assistant${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      "affiliate-user-id": localStorage.getItem("appliedAffiliate") ?? ""
    },
    credentials: "include",
    body: JSON.stringify(data)
  })

  if (!response.ok || !response.body) throw new Error(`assistant stream failed with ${response.status}`)

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ""
  let action: string | null = null
  // Only the cover action carries one: the reply offering artwork is often the answer to "which
  // release?", so the id is the only thing saying which one the artist settled on.
  let releaseId: number | null = null
  // Which reporting month the download action hands over, YYYYMM. The server only sends one it has a
  // report for, so the button never downloads an empty file.
  let reportPeriod: string | null = null
  // Account details read out of an attached image, already checked against the IBAN's own checksum.
  // They fill a form in the chat and are saved only when the artist presses the button on it.
  let bankDetails: CoManagerBankDetails | null = null
  // A request to Kahuna support drafted off an attached YouTube claim, or off a Content ID question
  // the assistant passed on. Editable in the chat, and sent only when the artist presses its button.
  let claimSupport: CoManagerClaimSupport | null = null
  // Which language the reply came back in. The assistant follows the artist's language, which is not
  // necessarily the language the interface is set to, and our own strings have to match the bubble
  // they sit next to. Null means undetected, and the interface language stands.
  let locale: string | null = null

  // Events arrive as "data: {...}\n\n" and a network chunk can cut one in half, so only whole events
  // separated by a blank line are parsed and the remainder waits for the next read.
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })

    let boundary = buffer.indexOf("\n\n")
    while (boundary !== -1) {
      const raw = buffer.slice(0, boundary).replace(/^data: ?/, "")
      buffer = buffer.slice(boundary + 2)
      boundary = buffer.indexOf("\n\n")

      if (!raw) continue

      const event = JSON.parse(raw)
      if (event.type === "delta") onDelta(event.text)
      else if (event.type === "done") {
        action = event.action ?? null
        releaseId = event.release_id ?? null
        reportPeriod = event.report_period ?? null
        bankDetails = event.bank_details ? mapToCamelCase<CoManagerBankDetails>(event.bank_details) : null
        claimSupport = event.claim_support ? mapToCamelCase<CoManagerClaimSupport>(event.claim_support) : null
        locale = event.locale ?? null
      } else if (event.type === "error") throw new Error(event.error)
    }
  }

  return { action, releaseId, reportPeriod, bankDetails, claimSupport, locale }
}
export const affiliate = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/affiliate${path}`, { method, data }, true)
export const userRegistration = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/user-registration${path}`, { method, data }, true)
export const notifications = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/notifications${path}`, { method, data }, true)
export const announcements = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/announcements${path}`, { method, data }, true)
export const trackPlaylist = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/insights/track-playlist${path}`, { method, data }, true)
export const spotifyChartEntries = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/insights/spotify-chart-entries${path}`, { method, data }, true)
export const marketingMilestoneEvents = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/insights/marketing-milestone-events${path}`, { method, data }, true)
export const marketingMilestoneRules = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/insights/marketing-milestone-rules${path}`, { method, data }, true)
export const marketingTopTracks = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/insights/marketing-top-tracks${path}`, { method, data }, true)
export const systems = (path: string, method: Method, data: any = null) =>
  createAuthorizedRequest(`/system${path}`, { method, data }, false)

export const handleAuthorizedRequest = async (
  callback: () => Promise<any>,
  casualErrorMessage: string,
  dispatch: Dispatch<Action> = null
) => {
  try {
    return await callback()
  } catch (e) {
    if (e.name === UNAUTHORIZED_ERROR_NAME || e.response?.data?.action === "logout") {
      console.error("Incorrect authorization details. Logging out...")
      if (dispatch) {
        dispatch({
          type: AUTH_ACTION.LOGOUT
        })
      } else {
        localStorage.setItem("user", "{}")
        localStorage.setItem("token", "")
      }
      window.location.pathname = "/login"
    }
    if (e.message === "canceled" || e.code === "ERR_CANCELED" || e.name === "CanceledError") {
      // Request was aborted, silently ignore
      return
    }
    console.error(e)
    toast.error(casualErrorMessage, errorFilledToastOptions)
  }
}
